import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Container, Box, Skeleton, Typography, Button, Alert } from '@mui/material';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Page } from '../../../components';
import HeaderBreadcrumbs from '../../../components/header-breadcrumbs';
import { RootState } from '../../../store';
import { MyDashboardService } from '../../../services/my-dashboard.service';
import { MyDashboardTypes } from '../../../types/my-dashboard.types';
import { AlertTypes } from '../../../types/alert.types';
import { PeopleZone } from './zones/PeopleZone';
import { AlertsZone } from './zones/AlertsZone';
import { WorkflowZone } from './zones/WorkflowZone';
import { ActiveAlertsZone } from './zones/ActiveAlertsZone';
import { DrillDownModal } from './components/DrillDownModal';
import { ScopeBanner } from './components/ScopeBanner';
import { AlertDetailsDrawer } from '../../../components/details-drawers/alert-details-drawer';
import { AppointmentDrawer } from '../../../components/details-drawers/apppointments-drawer';
import { AppointmentTypes } from '../../../types/appointment.types';
import { AppointmentService } from '../../../services/appointment.service';

const ALERT_METRICS: MyDashboardTypes.MetricKey[] = [
    'sosAlerts', 'checkOnMe', 'fakeCalls', 'pendingRiskReviews', 'totalRiskReviews',
];

const ACTIVE_EVENTS_POLL_MS = 30_000;
// Exponential back-off on consecutive active-events fetch failures: doubles
// each failure up to ACTIVE_EVENTS_BACKOFF_MAX_MS. Resets on success.
const ACTIVE_EVENTS_BACKOFF_MAX_MS = 5 * 60_000;

export const MyDashboardPage: React.FC = () => {
    const loadingSummary = useSelector((s: RootState) => s.myDashboard.loading.summary);
    const summaryError = useSelector((s: RootState) => s.myDashboard.summaryError);
    const summary = useSelector((s: RootState) => s.myDashboard.summary);
    const activeAlerts = useSelector((s: RootState) => s.myDashboard.activeEvents);
    const activeAlertsLoading = useSelector((s: RootState) => s.myDashboard.loading.activeEvents);
    const activeEventsLastFetch = useSelector((s: RootState) => s.myDashboard.activeEventsLastFetch);
    const activeEventsError = useSelector((s: RootState) => s.myDashboard.activeEventsError);

    // Alert details drawer
    const [alertDrawerVisible, setAlertDrawerVisible] = useState(false);
    const [selectedAlert, setSelectedAlert] = useState<AlertTypes.Model | null>(null);

    // Appointment details drawer
    const [apptDrawerVisible, setApptDrawerVisible] = useState(false);
    const [selectedAppt, setSelectedAppt] = useState<AppointmentTypes.Model | null>(null);

    // Drill-down modal
    const [drillDownOpen, setDrillDownOpen] = useState(false);
    const [drillDownMetric, setDrillDownMetric] = useState<MyDashboardTypes.MetricKey | null>(null);
    // Monotonic token for in-flight row-click fetches. Incremented on every
    // new click and on modal close; the async resolution checks its captured
    // token against this ref and bails out if it's stale — stops us from
    // opening a drawer the user already dismissed.
    const rowClickTokenRef = useRef(0);

    useEffect(() => {
        MyDashboardService.fetchScope();
        MyDashboardService.fetchSummary();
        MyDashboardService.fetchActiveEvents();

        // Self-scheduling poll (setTimeout) so we can extend the delay after
        // failures without clearing a fixed interval. consecutiveFailures
        // drives exponential back-off.
        let timer: ReturnType<typeof setTimeout> | null = null;
        let consecutiveFailures = 0;
        let cancelled = false;

        const scheduleNext = () => {
            // Also guard on visibility — an in-flight tick that resolves
            // after the tab went hidden would otherwise re-arm the timer
            // and keep polling silently in the background.
            if (cancelled || document.visibilityState !== 'visible') return;
            if (timer !== null) clearTimeout(timer);
            const backoff = Math.min(
                ACTIVE_EVENTS_POLL_MS * Math.pow(2, consecutiveFailures),
                ACTIVE_EVENTS_BACKOFF_MAX_MS,
            );
            timer = setTimeout(tick, backoff);
        };

        const tick = async () => {
            // try/finally guarantees scheduleNext() runs even if the fetch
            // throws something fetchActiveEvents didn't catch. Without this
            // a single unexpected error would silently kill the SOS poll
            // until the user refocuses the tab.
            try {
                if (document.visibilityState === 'visible') {
                    const ok = await MyDashboardService.fetchActiveEvents(true);
                    consecutiveFailures = ok ? 0 : consecutiveFailures + 1;
                }
            } catch (err) {
                console.error('[MyDashboard] active-events tick threw unexpectedly', (err as any)?.message);
                consecutiveFailures++;
            } finally {
                scheduleNext();
            }
        };

        const stopPolling = () => {
            if (timer !== null) { clearTimeout(timer); timer = null; }
        };

        scheduleNext();
        const onVisibilityChange = async () => {
            if (document.visibilityState === 'visible') {
                // Refresh immediately on tab re-focus. Reset back-off first so
                // a fresh session doesn't inherit a stale failure streak, but
                // still feed this fetch's result into the counter — otherwise
                // repeated re-focus failures would bypass back-off entirely.
                //
                // scheduleNext() lives in finally (mirrors tick): if the catch
                // itself throws for any reason, we still re-arm the poll loop
                // instead of silently killing live SOS updates.
                consecutiveFailures = 0;
                try {
                    const ok = await MyDashboardService.fetchActiveEvents(true);
                    if (!ok) consecutiveFailures++;
                } catch (err) {
                    console.error('[MyDashboard] re-focus fetch threw unexpectedly', (err as any)?.message);
                    consecutiveFailures++;
                } finally {
                    scheduleNext();
                }
            } else {
                stopPolling();
            }
        };
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            cancelled = true;
            stopPolling();
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, []);

    const handleMetricClick = useCallback((key: MyDashboardTypes.MetricKey) => {
        setDrillDownMetric(key);
        setDrillDownOpen(true);
        MyDashboardService.fetchDrillDown(key, 0, 25);
    }, []);

    const handleDrillDownClose = useCallback(() => {
        // Invalidate any in-flight row click so a late-arriving fetch can't
        // pop a drawer after the user dismissed the modal.
        rowClickTokenRef.current++;
        // Only flip the open flag here — clearing metricKey in the same batch
        // would unmount the Dialog content before MUI's fade-out transition
        // runs. handleDrillDownExited clears metricKey after onExited fires.
        setDrillDownOpen(false);
    }, []);

    const handleDrillDownExited = useCallback(() => {
        setDrillDownMetric(null);
    }, []);

    const handleRowClick = useCallback(async (metricKey: MyDashboardTypes.MetricKey, record: MyDashboardTypes.DrillDownRecord) => {
        if (ALERT_METRICS.includes(metricKey)) {
            // Drill-down always returns the full normalized alert document
            // (queryAlerts in my-dashboard.service returns the whole doc plus a
            // derived userName; the table's COLUMN_CONFIG only controls what's
            // *displayed*, not what's fetched). The cast is safe and the drawer
            // gets alertType / escalated / locations / mobileLocation etc.
            setDrillDownOpen(false);
            setSelectedAlert(record as unknown as AlertTypes.Model);
            setAlertDrawerVisible(true);
        } else if (metricKey === 'appointments') {
            // Fetch the enriched appointment first, THEN swap modal → drawer.
            // Closing the modal up-front would leave the user with nothing
            // on screen if the fetch fails or returns null.
            //
            // Capture a token before the await so that if the user dismisses
            // the drill-down modal (which bumps the ref) while the fetch is
            // in flight, we silently drop the result instead of popping a
            // drawer they didn't ask for anymore.
            const token = ++rowClickTokenRef.current;
            try {
                const appt = await AppointmentService.fetchById(record.id);
                if (token !== rowClickTokenRef.current) return;
                if (appt) {
                    setSelectedAppt(appt);
                    setDrillDownOpen(false);
                    setApptDrawerVisible(true);
                } else {
                    toast('Appointment not found.', { autoClose: 2500, type: 'error' });
                }
            } catch (err) {
                if (token !== rowClickTokenRef.current) return;
                console.error('[MyDashboard] Failed to load appointment', (err as any)?.message);
                toast('Couldn’t load appointment details. Try again in a moment.', { autoClose: 2500, type: 'error' });
            }
        }
        // Active/Inactive user rows don't currently navigate anywhere —
        // the drill-down table is the final surface until we add user-profile routing.
    }, []);

    const handleActiveAlertClick = useCallback((alert: AlertTypes.Model) => {
        if (alert.alertType === 'Appointment') {
            // The active-events feed synthesizes appointment "alerts" from
            // Appointment documents — it must carry appointmentId. Without it
            // we can't trust the row represents a real appointment, so surface
            // an error rather than silently opening a drawer keyed on the
            // alert's own id (which would break deep-linking / re-fetch).
            if (!alert.appointmentId) {
                console.error('[MyDashboard] Active appointment event missing appointmentId', alert.id);
                toast('Couldn’t open appointment — event is missing its appointment reference.', { autoClose: 2500, type: 'error' });
                return;
            }
            // Map active event to appointment model shape. isInProgress MUST be
            // true so the drawer's context fork lands on 'live' — otherwise the
            // endDateTime check falls through to 'past' and the live-status
            // badge / actions are wrong. endDateTime comes from the scheduled
            // end in the alert payload; if absent we omit it (leaves the field
            // undefined — matches the optional type on Model.endDateTime).
            //
            // endDate is REQUIRED by AppointmentTypes.Model (non-optional Date),
            // so we always populate it — falling back to startDate when the
            // scheduled end is missing. This is an honest "unknown end" signal
            // rather than `new Date()` which would shift the entity into "past"
            // territory for any consumer that checks endDate < now without
            // also honoring isInProgress.
            const startDate = new Date(alert.initiatedAt);
            const scheduledEnd = alert.appointmentEndDateTime
                ? new Date(alert.appointmentEndDateTime)
                : undefined;
            // Only build a coordinates object when we actually have a lat/lng
            // from the alert's mobileLocation. Zero-filling would render as a
            // literal "0, 0" string (Gulf of Guinea) in the drawer's location
            // fallback text, which is worse than no coordinates at all.
            const mobileLat = alert.mobileLocation?.latitude;
            const mobileLng = alert.mobileLocation?.longitude;
            const coordinates = mobileLat && mobileLng
                ? { latitude: Number(mobileLat), longitude: Number(mobileLng) }
                : null;
            setSelectedAppt({
                id: alert.appointmentId,
                title: alert.appointmentTitle || 'Appointment',
                userId: alert.userId,
                status: alert.status,
                isInProgress: true,
                startDate,
                endDate: scheduledEnd ?? startDate,
                startDateTime: startDate,
                ...(scheduledEnd ? { endDateTime: scheduledEnd } : {}),
                notes: '',
                location: alert.address || alert.mobileLocation?.address || '',
                coordinates,
                user: alert.user || {} as any,
                createdAt: startDate,
            } as AppointmentTypes.Model);
            setApptDrawerVisible(true);
        } else {
            setSelectedAlert(alert);
            setAlertDrawerVisible(true);
        }
    }, []);

    return (
        <Page title="POM Safe - My Dashboard">
            <Container maxWidth={false}>
                <HeaderBreadcrumbs
                    heading="My Dashboard"
                    links={[]}
                    activeLast={false}
                />

                <ScopeBanner />

                {loadingSummary ? (
                    <DashboardSkeleton />
                ) : summaryError && !summary ? (
                    <Alert
                        severity="error"
                        action={
                            <Button
                                size="small"
                                color="inherit"
                                startIcon={<RefreshIcon />}
                                onClick={() => MyDashboardService.fetchSummary()}
                            >
                                Retry
                            </Button>
                        }
                        sx={{ mt: 2 }}
                    >
                        Couldn't load dashboard data. Check your connection and try again.
                    </Alert>
                ) : (
                    <>
                        {/* Section 1: Live */}
                        <SectionHeader label="Happening Now" badge="LIVE" badgeColor="#16A34A" pulse />
                        <ActiveAlertsZone
                            alerts={activeAlerts}
                            loading={activeAlertsLoading}
                            onAlertClick={handleActiveAlertClick}
                            lastFetchAt={activeEventsLastFetch}
                            hasError={activeEventsError}
                            onRetry={() => MyDashboardService.fetchActiveEvents()}
                        />

                        {/* Section 2: Weekly summary */}
                        <SectionHeader label="This Week at a Glance" badge="7D" badgeColor="#206FA5" sx={{ mt: 4 }} />
                        <Box sx={{ display: 'flex', gap: '18px', flexWrap: 'wrap', alignItems: 'stretch', mt: '12px' }}>
                            <PeopleZone onMetricClick={handleMetricClick} />
                            <AlertsZone onMetricClick={handleMetricClick} />
                            <WorkflowZone onMetricClick={handleMetricClick} />
                        </Box>
                    </>
                )}

                <DrillDownModal
                    open={drillDownOpen}
                    metricKey={drillDownMetric}
                    onClose={handleDrillDownClose}
                    onExited={handleDrillDownExited}
                    onRowClick={handleRowClick}
                />

                {selectedAlert && (
                    <AlertDetailsDrawer
                        visible={alertDrawerVisible}
                        onClose={() => setAlertDrawerVisible(false)}
                        alert={selectedAlert}
                        type={selectedAlert.alertType}
                    />
                )}

                {selectedAppt && (
                    <AppointmentDrawer
                        visible={apptDrawerVisible}
                        onClose={() => setApptDrawerVisible(false)}
                        selectedAppointment={selectedAppt}
                    />
                )}
            </Container>
        </Page>
    );
};

function SectionHeader({ label, badge, badgeColor, pulse, sx }: {
    label: string;
    badge: string;
    badgeColor: string;
    pulse?: boolean;
    sx?: Record<string, any>;
}) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, ...sx }}>
            <Typography sx={{
                fontSize: 11, fontWeight: 700, color: '#64748B',
                letterSpacing: '0.12em', textTransform: 'uppercase',
            }}>
                {label}
            </Typography>
            <Box sx={{
                fontSize: 10, fontWeight: 700, color: badgeColor,
                bgcolor: `${badgeColor}12`, border: `1px solid ${badgeColor}30`,
                px: 1, py: 0.25, borderRadius: 10,
                display: 'flex', alignItems: 'center', gap: 0.5,
            }}>
                {pulse && (
                    <Box sx={{
                        width: 6, height: 6, borderRadius: '50%', bgcolor: badgeColor,
                        animation: 'sectionPulse 2s ease-in-out infinite',
                        '@keyframes sectionPulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.4 },
                        },
                    }} />
                )}
                {badge}
            </Box>
        </Box>
    );
}

function DashboardSkeleton() {
    return (
        <Box sx={{ display: 'flex', gap: '18px', flexWrap: 'wrap' }}>
            {[0, 1, 2].map((i) => (
                <Box key={i} sx={{
                    flex: '1 1 280px', minWidth: 260, minHeight: 400,
                    bgcolor: '#fff', border: '1px solid #E8ECF0', borderRadius: '14px',
                    p: '22px',
                }}>
                    <Skeleton variant="text" width="40%" height={20} sx={{ mb: 2 }} />
                    <Skeleton variant="circular" width={84} height={84} sx={{ mb: 2 }} />
                    <Skeleton variant="text" width="60%" height={40} sx={{ mb: 2 }} />
                    <Skeleton variant="rectangular" height={44} sx={{ borderRadius: 1, mb: 1 }} />
                    <Skeleton variant="rectangular" height={44} sx={{ borderRadius: 1, mb: 1 }} />
                    <Skeleton variant="rectangular" height={44} sx={{ borderRadius: 1 }} />
                </Box>
            ))}
        </Box>
    );
}
